/**
 * BUSINESS LAYER (BL): QUICK REACTION (REACCIÓN RÁPIDA 1 VS 1)
 * Capa de lógica de negocio, validaciones defensivas, persistencia de sesión móvil de jugador,
 * motor de síntesis Web Audio nativo (cero dependencias externas ni archivos MP3),
 * gestión de bloqueo de pantalla (Screen Wake Lock API), vibración háptica y calibración de reloj NTP.
 * 
 * @module utilities/BL/QuickReaction
 */

import {
    GetThemesDL,
    GetThemeByIdDL,
    CreateThemeDL,
    UpdateThemeDL,
    DeleteThemeDL,
    CloneThemeDL,
    GetConfigDL,
    SaveConfigDL,
    GetMatchHistoryDL,
    SaveMatchResultDL,
    UploadAssetDL,
    UploadBatchMediaDL,
    ShowCoverDL,
    ShowGameSceneDL,
    StartMatchDL,
    ResetMatchDL,
    FinishEarlyDL,
    GetMatchStateDL,
    SelectPlayerSideDL,
    SendTapDL,
    UpdatePlayersAndModeDL,
    UnregisterFanDL
} from '../DL/QuickReaction';

// =========================================================================
// CONSTANTES Y POLÍTICAS DE SEGURIDAD DEFENSIVA (APPSEC / OWASP)
// =========================================================================

/**
 * Lista blanca estricta de tipos MIME de imagen permitidos para subida multimedia.
 * @constant {Array<string>}
 */
export const ALLOWED_IMAGE_MIME_TYPES = [
    'image/png',
    'image/jpeg',
    'image/jpg',
    'image/webp',
    'image/svg+xml',
    'image/gif'
];

/**
 * Lista blanca de extensiones de archivo de imagen permitidas.
 * @constant {Array<string>}
 */
export const ALLOWED_IMAGE_EXTENSIONS = ['png', 'jpg', 'jpeg', 'webp', 'svg', 'gif'];

/**
 * Tamaño máximo permitido para archivos individuales (15 MB).
 * @constant {number}
 */
export const MAX_UPLOAD_FILE_SIZE_BYTES = 15 * 1024 * 1024;

/**
 * Límite máximo de cantidad de archivos para procesamiento por lote (batch upload).
 * @constant {number}
 */
export const MAX_BATCH_FILES_COUNT = 50;

/**
 * Tamaño máximo acumulado permitido para lotes multimedia (100 MB).
 * @constant {number}
 */
export const MAX_BATCH_TOTAL_SIZE_BYTES = 100 * 1024 * 1024;

/**
 * Sanea y valida identificadores alfanuméricos y GUIDs para prevenir inyecciones CRLF y manipulación de rutas.
 * 
 * @param {string|null} identifier - Identificador a sanear.
 * @param {number} [maxLength=100] - Longitud máxima permitida.
 * @returns {string} Identificador limpio y seguro o cadena vacía si es inválido.
 */
export const SanitizeIdentifier = (identifier, maxLength = 100) => {
    if (identifier === null || identifier === undefined) return '';
    const str = String(identifier).replace(/[\r\n\t\0]/g, '').trim();
    if (!str) return '';
    if (!/^[a-zA-Z0-9_\-:]+$/.test(str)) {
        return str.replace(/[^a-zA-Z0-9_\-:]/g, '').substring(0, maxLength);
    }
    return str.substring(0, maxLength);
};

/**
 * Sanea un identificador de sala (RoomId).
 * @param {string|null} roomId - Identificador GUID o slug de la sala.
 * @returns {string} RoomId saneado.
 */
export const SanitizeRoomId = (roomId) => SanitizeIdentifier(roomId, 100);

/**
 * Sanea texto de entrada para nombres de temas, jugadores o descripciones.
 * Elimina caracteres de control y limita longitud para prevenir DoS y contaminación de datos.
 * 
 * @param {string} text - Texto a sanear.
 * @param {number} [maxLength=200] - Longitud máxima permitida.
 * @returns {string} Texto saneado.
 */
export const SanitizeText = (text, maxLength = 200) => {
    if (!text || typeof text !== 'string') return '';
    return text
        .replace(/[\r\n\t\0]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
        .substring(0, maxLength);
};

/**
 * Sanea el nombre o alias de un jugador aficionado.
 * @param {string} name - Nombre del jugador.
 * @returns {string} Nombre saneado.
 */
export const SanitizePlayerName = (name) => SanitizeText(name, 100);

/**
 * Valida y sanea URLs de recursos multimedia para prevenir inyecciones XSS (javascript:, vbscript:, etc.)
 * y Directory Traversal en rutas relativas.
 * 
 * @param {string} url - URL o ruta relativa a validar.
 * @returns {string} URL validada o cadena vacía si es insegura.
 */
export const SanitizeAssetUrl = (url) => {
    if (!url || typeof url !== 'string') return '';
    const trimmed = url.trim().replace(/[\r\n\t\0]/g, '');
    if (!trimmed || trimmed.length > 2048) return '';

    const lower = trimmed.toLowerCase();
    // Prohibir esquemas peligrosos ejecutables y protocolos no permitidos
    if (
        lower.startsWith('javascript:') ||
        lower.startsWith('vbscript:') ||
        lower.startsWith('file:') ||
        lower.startsWith('data:text/') ||
        lower.startsWith('data:application/')
    ) {
        console.warn('[QuickReactionBL] URL bloqueada por protocolo peligroso:', trimmed.substring(0, 50));
        return '';
    }

    // Prevenir ruptura de encapsulación CSS url(...) o atributos HTML (comillas, paréntesis, backslash, tags)
    if (/["'\\()<>]/.test(trimmed)) {
        console.warn('[QuickReactionBL] URL bloqueada por contener caracteres no permitidos para contexto CSS/HTML.');
        return '';
    }

    // Validar data URIs para que solo contengan imágenes permitidas
    if (lower.startsWith('data:')) {
        const isSafeDataImage = /^data:image\/(png|jpeg|jpg|webp|gif|svg\+xml);base64,[a-zA-Z0-9+/=]+$/i.test(trimmed);
        if (!isSafeDataImage) {
            console.warn('[QuickReactionBL] data: URI bloqueada por MIME no admitido o formato inválido.');
            return '';
        }
        return trimmed;
    }

    // Permitir blob:, http://, https://
    if (lower.startsWith('blob:') || lower.startsWith('http://') || lower.startsWith('https://')) {
        return trimmed;
    }

    // Para rutas relativas, prevenir Directory Traversal (../, ..\)
    if (trimmed.includes('../') || trimmed.includes('..\\')) {
        console.warn('[QuickReactionBL] Ruta relativa bloqueada por contener Directory Traversal (..)');
        return '';
    }

    return trimmed;
};

/**
 * Valida un archivo individual antes de su transmisión al servidor.
 * Verifica MIME type, extensión de archivo y límite de tamaño en bytes.
 * 
 * @param {File|Blob} file - Archivo a validar.
 * @returns {{isValid: boolean, message?: string}} Resultado de la validación.
 */
export const ValidateUploadFile = (file) => {
    if (!file) {
        return { isValid: false, message: 'No se ha seleccionado ningún archivo.' };
    }

    // Validación de tamaño máximo (15 MB)
    if (file.size !== undefined && file.size > MAX_UPLOAD_FILE_SIZE_BYTES) {
        const sizeMb = (file.size / (1024 * 1024)).toFixed(2);
        return {
            isValid: false,
            message: `El archivo seleccionado (${sizeMb} MB) excede el tamaño máximo permitido de 15 MB.`
        };
    }

    // Validación de extensión de archivo
    if (file.name && typeof file.name === 'string') {
        const extensionMatch = file.name.split('.').pop();
        const ext = (extensionMatch || '').toLowerCase();
        if (!ALLOWED_IMAGE_EXTENSIONS.includes(ext)) {
            return {
                isValid: false,
                message: `Extensión de archivo .${ext} no permitida. Solo se admiten: ${ALLOWED_IMAGE_EXTENSIONS.join(', ')}.`
            };
        }
    }

    // Validación de tipo MIME
    if (file.type && typeof file.type === 'string') {
        const normalizedMime = file.type.toLowerCase().split(';')[0].trim();
        if (!ALLOWED_IMAGE_MIME_TYPES.includes(normalizedMime)) {
            return {
                isValid: false,
                message: `Tipo de archivo (${file.type}) no admitido. Se requieren formatos de imagen válidos (PNG, JPEG, WebP, SVG, GIF).`
            };
        }
    }

    return { isValid: true };
};

/**
 * Valida una colección de archivos para subida en lote (batch upload).
 * Verifica límite de cantidad (máx 50), tamaño individual y tamaño acumulado (máx 100 MB).
 * 
 * @param {Array<File>|FileList} files - Colección de archivos a validar.
 * @returns {{isValid: boolean, message?: string}} Resultado de la validación.
 */
export const ValidateBatchUploadFiles = (files) => {
    if (!files || (Array.isArray(files) && files.length === 0)) {
        return { isValid: false, message: 'No se han seleccionado archivos para el lote.' };
    }

    const fileListArray = Array.from(files);
    if (fileListArray.length > MAX_BATCH_FILES_COUNT) {
        return {
            isValid: false,
            message: `Se ha superado el límite de ${MAX_BATCH_FILES_COUNT} archivos por lote (seleccionados: ${fileListArray.length}).`
        };
    }

    let totalSize = 0;
    for (let i = 0; i < fileListArray.length; i++) {
        const fileItem = fileListArray[i];
        const singleValidation = ValidateUploadFile(fileItem);
        if (!singleValidation.isValid) {
            return {
                isValid: false,
                message: `Archivo #${i + 1} "${fileItem.name || 'Sin nombre'}": ${singleValidation.message}`
            };
        }
        totalSize += fileItem.size || 0;
    }

    if (totalSize > MAX_BATCH_TOTAL_SIZE_BYTES) {
        const totalMb = (totalSize / (1024 * 1024)).toFixed(2);
        return {
            isValid: false,
            message: `El tamaño total del lote (${totalMb} MB) supera el límite máximo permitido de 100 MB.`
        };
    }

    return { isValid: true };
};

// =========================================================================
// GESTIÓN DE SESIÓN MÓVIL DEL JUGADOR (SESSION STORAGE NAMESPACE)
// =========================================================================

/**
 * Prefijo o namespace constante para el almacenamiento de sesión del aficionado móvil.
 * @constant {string}
 */
const SESSION_STORAGE_KEY_PREFIX = 'quick_reaction_fan_session_';

/**
 * Obtiene la clave de almacenamiento calificada para una sala.
 * 
 * @param {string} roomId - Identificador GUID de la sala.
 * @returns {string} Clave de sessionStorage calificada.
 */
const getSessionStorageKey = (roomId) => {
    const cleanRoomId = roomId ? SanitizeRoomId(roomId) : 'default';
    return `${SESSION_STORAGE_KEY_PREFIX}${cleanRoomId}`;
};

/**
 * Almacena los datos de la sesión del jugador móvil en sessionStorage de forma segura y validada.
 * 
 * @param {string} roomId - Identificador GUID de la sala.
 * @param {Object} sessionData - Objeto con los datos de sesión (side, playerName, avatarUrl, timestamp).
 * @returns {boolean} Verdadero si los datos fueron almacenados exitosamente.
 */
export const SavePlayerSessionData = (roomId, sessionData) => {
    try {
        if (typeof sessionStorage === 'undefined' || !sessionData || typeof sessionData !== 'object') {
            return false;
        }

        const sanitizedRoomId = SanitizeRoomId(roomId);
        if (!sanitizedRoomId) return false;

        const rawSide = String(sessionData.side || '').toLowerCase().trim();
        const validSide = (rawSide === 'player1' || rawSide === 'player2' || rawSide === 'j1' || rawSide === 'j2')
            ? (rawSide === 'player2' || rawSide === 'j2' ? 'player2' : 'player1')
            : '';

        const sanitizedPlayerName = SanitizePlayerName(sessionData.playerName || '');
        const storageKey = getSessionStorageKey(sanitizedRoomId);

        const serializedData = JSON.stringify({
            side: validSide,
            playerName: sanitizedPlayerName,
            connectionId: SanitizeIdentifier(sessionData.connectionId || ''),
            savedAt: Date.now()
        });

        sessionStorage.setItem(storageKey, serializedData);
        return true;
    } catch (storageError) {
        console.warn('[QuickReactionBL] Error al persistir sesión en sessionStorage:', storageError);
        return false;
    }
};

/**
 * Recupera los datos de la sesión del jugador móvil desde sessionStorage para una sala específica.
 * Aplica validación de esquema y expiración de 24 horas.
 * 
 * @param {string} roomId - Identificador GUID de la sala.
 * @returns {Object|null} Datos de la sesión del jugador o nulo si no existe o expiró.
 */
export const GetPlayerSessionData = (roomId) => {
    try {
        if (typeof sessionStorage === 'undefined') {
            return null;
        }

        const sanitizedRoomId = SanitizeRoomId(roomId);
        if (!sanitizedRoomId) return null;

        const storageKey = getSessionStorageKey(sanitizedRoomId);
        const storedRaw = sessionStorage.getItem(storageKey);

        if (!storedRaw) {
            return null;
        }

        const parsed = JSON.parse(storedRaw);
        if (!parsed || typeof parsed !== 'object') {
            ClearPlayerSessionData(sanitizedRoomId);
            return null;
        }

        // Expirar sesiones mayores a 24 horas
        const sessionAge = Date.now() - (Number(parsed.savedAt) || 0);
        if (sessionAge > 24 * 60 * 60 * 1000) {
            ClearPlayerSessionData(sanitizedRoomId);
            return null;
        }

        const rawSide = String(parsed.side || '').toLowerCase().trim();
        const cleanSide = (rawSide === 'player1' || rawSide === 'player2') ? rawSide : '';
        const cleanPlayerName = SanitizePlayerName(parsed.playerName || '');

        return {
            side: cleanSide,
            playerName: cleanPlayerName,
            connectionId: SanitizeIdentifier(parsed.connectionId || ''),
            savedAt: parsed.savedAt
        };
    } catch (storageError) {
        console.warn('[QuickReactionBL] Error al leer sesión de sessionStorage:', storageError);
        return null;
    }
};

/**
 * Limpia y remueve los datos de la sesión del jugador móvil en sessionStorage.
 * Se invoca obligatoriamente tras recibir el evento 'kicked_out' o al reiniciar voluntariamente.
 * 
 * @param {string} roomId - Identificador GUID de la sala.
 * @returns {boolean} Verdadero si la sesión fue eliminada correctamente.
 */
export const ClearPlayerSessionData = (roomId) => {
    try {
        if (typeof sessionStorage === 'undefined') {
            return false;
        }

        const sanitizedRoomId = SanitizeRoomId(roomId);
        const storageKey = getSessionStorageKey(sanitizedRoomId);
        sessionStorage.removeItem(storageKey);
        return true;
    } catch (storageError) {
        console.warn('[QuickReactionBL] Error al limpiar sesión de sessionStorage:', storageError);
        return false;
    }
};

// =========================================================================
// MOTOR DE SÍNTESIS DE AUDIO NATIVO (WEB AUDIO API - ZERO MP3)
// =========================================================================

/**
 * Instancia singleton de AudioContext para el motor de audio sintético.
 * @type {AudioContext|null}
 */
let sharedAudioContextInstance = null;

/**
 * Indicador de si el listener para desbloqueo automático ya fue adjuntado a la ventana.
 * @type {boolean}
 */
let isAutoUnlockAttached = false;

/**
 * Obtiene o crea la instancia de AudioContext compatible con múltiples navegadores.
 * 
 * @returns {AudioContext|null} Instancia activa de AudioContext.
 */
const getAudioContext = () => {
    if (typeof window === 'undefined' && typeof global === 'undefined') {
        return null;
    }

    const AudioContextClass = (typeof window !== 'undefined' ? (window.AudioContext || window.webkitAudioContext) : null) ||
        (typeof global !== 'undefined' ? (global.AudioContext || global.webkitAudioContext) : null);

    if (!AudioContextClass) {
        return null;
    }

    if (process.env.NODE_ENV === 'test') {
        try {
            return new AudioContextClass();
        } catch {
            return null;
        }
    }

    if (!sharedAudioContextInstance) {
        try {
            sharedAudioContextInstance = new AudioContextClass();
        } catch {
            sharedAudioContextInstance = null;
        }
    }

    return sharedAudioContextInstance;
};

/**
 * Inicializa o reanuda el AudioContext ante el primer gesto de usuario (click/touchstart/keydown).
 * Resuelve las restricciones de auto-reproducción (Autoplay Policy) de los navegadores móviles y de escritorio.
 * 
 * @returns {Promise<AudioContext|null>} Instancia de AudioContext en estado 'running'.
 */
export const UnlockAudioContext = async () => {
    try {
        const audioCtx = getAudioContext();
        if (!audioCtx) return null;

        if (audioCtx.state === 'suspended') {
            await audioCtx.resume();
        }

        return audioCtx;
    } catch (unlockError) {
        console.warn('[QuickReactionBL] No se pudo desbloquear el AudioContext:', unlockError);
        return null;
    }
};

/**
 * Adjunta listeners globales para desbloquear el AudioContext automáticamente en la primera interacción.
 */
export const AttachAutoUnlockAudioListeners = () => {
    if (isAutoUnlockAttached || typeof window === 'undefined') return;

    const unlockHandler = () => {
        UnlockAudioContext().then(() => {
            window.removeEventListener('click', unlockHandler);
            window.removeEventListener('touchstart', unlockHandler);
            window.removeEventListener('keydown', unlockHandler);
        });
    };

    window.addEventListener('click', unlockHandler, { once: true, passive: true });
    window.addEventListener('touchstart', unlockHandler, { once: true, passive: true });
    window.addEventListener('keydown', unlockHandler, { once: true, passive: true });

    isAutoUnlockAttached = true;
};

/**
 * Sintetiza efectos de sonido puros en tiempo real utilizando la Web Audio API nativa.
 * Sin descargas de red ni dependencias de archivos MP3/WAV externos.
 * 
 * Tipos de sonido soportados:
 * - 'COUNTDOWN_3': Oscilador senoidal (440Hz, La4) con decaimiento ADSR.
 * - 'COUNTDOWN_2': Oscilador senoidal (554.37Hz, Do#5) con decaimiento ADSR.
 * - 'COUNTDOWN_1': Oscilador senoidal (880Hz, La5) con decaimiento ADSR agudo.
 * - 'TENSION': Pulso rítmico sub-grave (60-80Hz) con filtro pasa-bajos.
 * - 'TRIGGER': Impulso percusivo de ruido blanco con filtro pasa-bajos ultra rápido (0ms latencia).
 * - 'VICTORY': Acordes mayores ascendentes sintetizados en arpegio brillante.
 * - 'FAULT': Tono disonante descendente de advertencia.
 * 
 * @param {string} soundType - Tipo de sonido a reproducir ('COUNTDOWN_3' | 'COUNTDOWN_2' | 'COUNTDOWN_1' | 'TENSION' | 'TRIGGER' | 'VICTORY' | 'FAULT').
 * @returns {boolean} Verdadero si el sonido fue sintetizado y programado correctamente.
 */
export const SynthesizeQuickReactionSound = (soundType) => {
    try {
        const audioCtx = getAudioContext();
        if (!audioCtx) return false;

        // Si el contexto está suspendido, intentar reanudarlo
        if (audioCtx.state === 'suspended') {
            audioCtx.resume().catch(() => {});
        }

        const now = audioCtx.currentTime;
        const normalizedType = String(soundType || '').toUpperCase().trim();

        switch (normalizedType) {
            case 'COUNTDOWN_3': {
                // Tono 440 Hz (La4) - Ataque suave y decaimiento exponencial
                const osc = audioCtx.createOscillator();
                const gain = audioCtx.createGain();

                osc.type = 'sine';
                osc.frequency.setValueAtTime(440, now);

                gain.gain.setValueAtTime(0.001, now);
                gain.gain.exponentialRampToValueAtTime(0.4, now + 0.02);
                gain.gain.exponentialRampToValueAtTime(0.001, now + 0.28);

                osc.connect(gain);
                gain.connect(audioCtx.destination);

                osc.start(now);
                osc.stop(now + 0.3);
                return true;
            }

            case 'COUNTDOWN_2': {
                // Tono 554.37 Hz (Do#5) - Mayor urgencia
                const osc = audioCtx.createOscillator();
                const gain = audioCtx.createGain();

                osc.type = 'sine';
                osc.frequency.setValueAtTime(554.37, now);

                gain.gain.setValueAtTime(0.001, now);
                gain.gain.exponentialRampToValueAtTime(0.45, now + 0.02);
                gain.gain.exponentialRampToValueAtTime(0.001, now + 0.28);

                osc.connect(gain);
                gain.connect(audioCtx.destination);

                osc.start(now);
                osc.stop(now + 0.3);
                return true;
            }

            case 'COUNTDOWN_1': {
                // Tono 880 Hz (La5) - Agudo y penetrante para alerta final
                const osc = audioCtx.createOscillator();
                const gain = audioCtx.createGain();

                osc.type = 'sine';
                osc.frequency.setValueAtTime(880, now);

                gain.gain.setValueAtTime(0.001, now);
                gain.gain.exponentialRampToValueAtTime(0.5, now + 0.015);
                gain.gain.exponentialRampToValueAtTime(0.001, now + 0.35);

                osc.connect(gain);
                gain.connect(audioCtx.destination);

                osc.start(now);
                osc.stop(now + 0.37);
                return true;
            }

            case 'TENSION': {
                // Pulso rítmico sub-grave (60-80Hz) con filtro pasa-bajos envolvente
                const osc = audioCtx.createOscillator();
                const filter = audioCtx.createBiquadFilter();
                const gain = audioCtx.createGain();

                osc.type = 'triangle';
                osc.frequency.setValueAtTime(80, now);
                osc.frequency.exponentialRampToValueAtTime(55, now + 0.25);

                filter.type = 'lowpass';
                filter.frequency.setValueAtTime(140, now);
                filter.frequency.exponentialRampToValueAtTime(60, now + 0.25);

                gain.gain.setValueAtTime(0.001, now);
                gain.gain.exponentialRampToValueAtTime(0.6, now + 0.03);
                gain.gain.exponentialRampToValueAtTime(0.001, now + 0.3);

                osc.connect(filter);
                filter.connect(gain);
                gain.connect(audioCtx.destination);

                osc.start(now);
                osc.stop(now + 0.32);
                return true;
            }

            case 'TRIGGER': {
                // Impulso percusivo de ruido blanco con filtro pasa-bajos ultra rápido (0ms latencia)
                const bufferSize = audioCtx.sampleRate * 0.15; // 150ms de ruido
                const noiseBuffer = audioCtx.createBuffer(1, bufferSize, audioCtx.sampleRate);
                const outputData = noiseBuffer.getChannelData(0);

                for (let i = 0; i < bufferSize; i++) {
                    outputData[i] = Math.random() * 2 - 1;
                }

                const noiseSource = audioCtx.createBufferSource();
                noiseSource.buffer = noiseBuffer;

                const filter = audioCtx.createBiquadFilter();
                filter.type = 'lowpass';
                filter.frequency.setValueAtTime(4500, now);
                filter.frequency.exponentialRampToValueAtTime(100, now + 0.12);

                const noiseGain = audioCtx.createGain();
                noiseGain.gain.setValueAtTime(0.9, now);
                noiseGain.gain.exponentialRampToValueAtTime(0.001, now + 0.14);

                noiseSource.connect(filter);
                filter.connect(noiseGain);
                noiseGain.connect(audioCtx.destination);

                // Capa de impacto tonal simultáneo (onda de choque corta 150Hz -> 40Hz)
                const subOsc = audioCtx.createOscillator();
                const subGain = audioCtx.createGain();

                subOsc.type = 'sine';
                subOsc.frequency.setValueAtTime(220, now);
                subOsc.frequency.exponentialRampToValueAtTime(40, now + 0.1);

                subGain.gain.setValueAtTime(0.8, now);
                subGain.gain.exponentialRampToValueAtTime(0.001, now + 0.12);

                subOsc.connect(subGain);
                subGain.connect(audioCtx.destination);

                noiseSource.start(now);
                noiseSource.stop(now + 0.15);
                subOsc.start(now);
                subOsc.stop(now + 0.13);
                return true;
            }

            case 'VICTORY': {
                // Acordes mayores ascendentes sintetizados (Do5, Mi5, Sol5, Do6)
                const notes = [
                    { freq: 523.25, timeOffset: 0.00, duration: 0.20 }, // C5
                    { freq: 659.25, timeOffset: 0.10, duration: 0.20 }, // E5
                    { freq: 783.99, timeOffset: 0.20, duration: 0.25 }, // G5
                    { freq: 1046.50, timeOffset: 0.32, duration: 0.60 } // C6 (brillante sostenido)
                ];

                notes.forEach((note) => {
                    const osc = audioCtx.createOscillator();
                    const gain = audioCtx.createGain();

                    osc.type = 'triangle';
                    osc.frequency.setValueAtTime(note.freq, now + note.timeOffset);

                    const startTime = now + note.timeOffset;
                    gain.gain.setValueAtTime(0.001, startTime);
                    gain.gain.exponentialRampToValueAtTime(0.35, startTime + 0.02);
                    gain.gain.exponentialRampToValueAtTime(0.001, startTime + note.duration);

                    osc.connect(gain);
                    gain.connect(audioCtx.destination);

                    osc.start(startTime);
                    osc.stop(startTime + note.duration);
                });

                return true;
            }

            case 'FAULT': {
                // Tono disonante de falta anticipada (sierra descendente 300Hz -> 80Hz)
                const osc = audioCtx.createOscillator();
                const gain = audioCtx.createGain();

                osc.type = 'sawtooth';
                osc.frequency.setValueAtTime(320, now);
                osc.frequency.linearRampToValueAtTime(90, now + 0.3);

                gain.gain.setValueAtTime(0.001, now);
                gain.gain.exponentialRampToValueAtTime(0.4, now + 0.02);
                gain.gain.exponentialRampToValueAtTime(0.001, now + 0.3);

                osc.connect(gain);
                gain.connect(audioCtx.destination);

                osc.start(now);
                osc.stop(now + 0.32);
                return true;
            }

            default:
                console.warn(`[QuickReactionBL] Tipo de sonido no reconocido: ${soundType}`);
                return false;
        }
    } catch (synthError) {
        console.warn('[QuickReactionBL] Error al sintetizar sonido:', synthError);
        return false;
    }
};

// =========================================================================
// SCREEN WAKE LOCK API (BLOQUEO DE APAGADO DE PANTALLA MÓVIL)
// =========================================================================

/**
 * Referencia al WakeLockSentinel activo.
 * @type {WakeLockSentinel|null}
 */
let screenWakeLockSentinel = null;

/**
 * Handler para re-adquirir el Wake Lock cuando la pestaña vuelve al primer plano.
 * @type {Function|null}
 */
let wakeLockVisibilityHandler = null;

/**
 * Solicita el bloqueo de apagado de pantalla (Screen Wake Lock) con re-adquisición automática ante cambios de visibilidad.
 * 
 * @returns {Promise<boolean>} Verdadero si el bloqueo de pantalla fue adquirido.
 */
export const RequestScreenWakeLock = async () => {
    try {
        if (typeof navigator === 'undefined' || !('wakeLock' in navigator)) {
            return false;
        }

        // Si ya hay un bloqueo activo, no duplicar
        if (screenWakeLockSentinel && !screenWakeLockSentinel.released) {
            return true;
        }

        screenWakeLockSentinel = await navigator.wakeLock.request('screen');

        // Configurar listener para re-adquirir cuando la pantalla vuelva a estar visible
        if (!wakeLockVisibilityHandler && typeof document !== 'undefined') {
            wakeLockVisibilityHandler = async () => {
                if (document.visibilityState === 'visible' && (!screenWakeLockSentinel || screenWakeLockSentinel.released)) {
                    try {
                        screenWakeLockSentinel = await navigator.wakeLock.request('screen');
                    } catch (reacquireErr) {
                        // Silencioso en caso de no poder re-adquirir en segundo plano
                    }
                }
            };
            document.addEventListener('visibilitychange', wakeLockVisibilityHandler);
        }

        return true;
    } catch (wakeLockError) {
        // Puede fallar si la batería es baja o el usuario denegó permisos
        console.warn('[QuickReactionBL] No se pudo activar Screen Wake Lock:', wakeLockError);
        return false;
    }
};

/**
 * Libera y desactiva el bloqueo de apagado de pantalla (Screen Wake Lock).
 * 
 * @returns {Promise<boolean>} Verdadero si fue liberado correctamente.
 */
export const ReleaseScreenWakeLock = async () => {
    try {
        if (wakeLockVisibilityHandler && typeof document !== 'undefined') {
            document.removeEventListener('visibilitychange', wakeLockVisibilityHandler);
            wakeLockVisibilityHandler = null;
        }

        if (screenWakeLockSentinel) {
            await screenWakeLockSentinel.release();
            screenWakeLockSentinel = null;
            return true;
        }

        return false;
    } catch (releaseError) {
        console.warn('[QuickReactionBL] Error al liberar Screen Wake Lock:', releaseError);
        screenWakeLockSentinel = null;
        return false;
    }
};

// =========================================================================
// VIBRACIÓN HÁPTICA (HAPTIC FEEDBACK)
// =========================================================================

/**
 * Ejecuta retroalimentación háptica (vibración) en dispositivos compatibles.
 * 
 * @param {number|Array<number>|string} [patternOrType=20] - Duración en ms, patrón de vibración o tipo semántico ('tap' | 'trigger' | 'fault' | 'victory').
 * @returns {boolean} Verdadero si la vibración fue invocada.
 */
export const TriggerHapticFeedback = (patternOrType = 20) => {
    try {
        const nav = (typeof window !== 'undefined' && window.navigator && window.navigator.vibrate)
            ? window.navigator
            : (typeof navigator !== 'undefined' && navigator.vibrate ? navigator : (typeof global !== 'undefined' && global.navigator ? global.navigator : null));

        if (!nav || typeof nav.vibrate !== 'function') {
            return false;
        }

        if (typeof patternOrType === 'string') {
            const normalized = patternOrType.toLowerCase().trim();
            switch (normalized) {
                case 'tap':
                    return nav.vibrate(20);
                case 'trigger':
                    return nav.vibrate(40);
                case 'fault':
                    return nav.vibrate([60, 40, 60, 40, 100]);
                case 'victory':
                    return nav.vibrate([40, 50, 40, 50, 120]);
                default:
                    return nav.vibrate(20);
            }
        }

        if (Array.isArray(patternOrType) || typeof patternOrType === 'number') {
            return nav.vibrate(patternOrType);
        }

        return false;
    } catch (vibrateError) {
        return false;
    }
};

// =========================================================================
// CALIBRACIÓN DE RELOJ NTP (SINCRONIZACIÓN DE TIEMPOS)
// =========================================================================

/**
 * Calcula el offset de reloj entre el cliente y el servidor para sincronización precisa de disparo.
 * Formula NTP: offset = serverTimestamp - ((clientSendTime + clientReceiveTime) / 2)
 * 
 * @param {number} serverTimestampMs - Marca de tiempo UTC emitida por el servidor.
 * @param {number} [clientSendTimestampMs=0] - Marca de tiempo en que el cliente emitió la petición.
 * @param {number} [clientReceiveTimestampMs=0] - Marca de tiempo en que el cliente recibió la respuesta.
 * @returns {{offsetMs: number, roundTripLatencyMs: number}} Offset de reloj y latencia estimada de ida y vuelta.
 */
export const CalibrateNtpOffset = (serverTimestampMs, clientSendTimestampMs = 0, clientReceiveTimestampMs = 0) => {
    const sendTime = clientSendTimestampMs || Date.now();
    const receiveTime = clientReceiveTimestampMs || Date.now();
    const roundTrip = Math.max(0, receiveTime - sendTime);
    const estimatedServerTimeAtReceive = serverTimestampMs + Math.floor(roundTrip / 2);
    const offset = estimatedServerTimeAtReceive - receiveTime;

    return {
        offsetMs: offset,
        roundTripLatencyMs: roundTrip
    };
};

// =========================================================================
// VALIDACIÓN DE TEMAS VISUALES
// =========================================================================

/**
 * Valida la integridad de la estructura, campos obligatorios y seguridad de URLs de un tema de Reacción Rápida.
 * 
 * @param {Object} theme - Objeto de datos del tema a validar.
 * @returns {{isValid: boolean, errors: Array<string>, message: string}} Resultado de la validación.
 */
export const ValidateQuickReactionTheme = (theme) => {
    const errors = [];
    if (!theme || typeof theme !== 'object') {
        return {
            isValid: false,
            errors: ['El objeto tema es obligatorio.'],
            message: 'El objeto tema es obligatorio.'
        };
    }

    const trimmedName = typeof theme.name === 'string' ? SanitizeText(theme.name, 200) : '';
    if (!trimmedName) {
        errors.push('El nombre del tema es obligatorio.');
    } else if (trimmedName.length > 200) {
        errors.push('El nombre del tema no puede exceder los 200 caracteres.');
    }

    // Validar que las URLs provistas para fondos y sprites no contengan esquemas inseguros o traversal
    const urlFieldKeys = [
        'screenBackgroundImageUrl',
        'mobileBackgroundImageUrl',
        'logoImageUrl',
        'j1SpriteBackUrl',
        'j1SpriteFrontUrl',
        'j1SpriteShootUrl',
        'j1SpriteVictoryUrl',
        'j2SpriteBackUrl',
        'j2SpriteFrontUrl',
        'j2SpriteShootUrl',
        'j2SpriteVictoryUrl'
    ];

    for (const field of urlFieldKeys) {
        const rawUrl = theme[field] || theme[field.charAt(0).toUpperCase() + field.slice(1)];
        if (rawUrl && typeof rawUrl === 'string' && rawUrl.trim() !== '') {
            const sanitized = SanitizeAssetUrl(rawUrl);
            if (!sanitized) {
                errors.push(`El recurso multimedia "${field}" contiene una ruta o protocolo no permitido.`);
            }
        }
    }

    return {
        isValid: errors.length === 0,
        errors: errors,
        message: errors.length > 0 ? errors.join(' ') : ''
    };
};

// =========================================================================
// ENVOLTORIOS DE LÓGICA DE NEGOCIO (CRUD DE TEMAS)
// =========================================================================

/**
 * Obtiene el catálogo completo de temas visuales para Reacción Rápida con validaciones.
 * 
 * @param {string|null} [roomId=null] - Identificador GUID de la sala (opcional).
 * @param {string|null} [operatorId=null] - Identificador GUID del operador (opcional).
 * @returns {Promise<{success: boolean, data: Array<Object>, message?: string}>} Colección garantizada de temas.
 */
export const GetThemesBL = async (roomId = null, operatorId = null) => {
    try {
        const sanitizedRoomId = SanitizeRoomId(roomId);
        const sanitizedOperatorId = SanitizeIdentifier(operatorId);

        const dlResponse = await GetThemesDL(sanitizedRoomId || null, sanitizedOperatorId || null);
        if (!dlResponse.success) {
            return {
                success: false,
                data: [],
                message: dlResponse.message || 'No fue posible cargar los temas de Reacción Rápida.'
            };
        }

        return {
            success: true,
            data: Array.isArray(dlResponse.data) ? dlResponse.data : []
        };
    } catch (error) {
        return {
            success: false,
            data: [],
            message: 'Error inesperado al consultar los temas de Reacción Rápida.'
        };
    }
};

/**
 * Obtiene el detalle de un tema específico validando el identificador.
 * 
 * @param {string} themeIdentifier - Identificador GUID del tema.
 * @param {string|null} [roomId=null] - Identificador opcional de la sala.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Detalle del tema.
 */
export const GetThemeByIdBL = async (themeIdentifier, roomId = null) => {
    const sanitizedThemeId = SanitizeIdentifier(themeIdentifier);
    if (!sanitizedThemeId) {
        return {
            success: false,
            message: 'El identificador del tema es obligatorio.'
        };
    }

    const sanitizedRoomId = SanitizeRoomId(roomId);
    return await GetThemeByIdDL(sanitizedThemeId, sanitizedRoomId || null);
};

/**
 * Valida y crea un nuevo tema personalizado para Reacción Rápida.
 * 
 * @param {Object} themePayload - Datos y URLs de sprites del nuevo tema.
 * @param {string|null} [explicitRoomId=null] - Identificador opcional de sala.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Tema creado.
 */
export const CreateThemeBL = async (themePayload, explicitRoomId = null) => {
    if (!themePayload || typeof themePayload !== 'object') {
        return {
            success: false,
            message: 'El cuerpo de datos del tema es obligatorio.'
        };
    }

    const validationResult = ValidateQuickReactionTheme(themePayload);
    if (!validationResult.isValid) {
        return {
            success: false,
            message: validationResult.message
        };
    }

    const trimmedName = SanitizeText(themePayload.name, 200);
    const resolvedRoomId = SanitizeRoomId(themePayload.roomId || explicitRoomId || null);
    const resolvedOperatorId = SanitizeIdentifier(themePayload.operatorId || null);

    const sanitizedPayload = {
        name: trimmedName,
        roomId: resolvedRoomId || null,
        operatorId: resolvedOperatorId || null,
        isSystemPreset: false,
        screenBackgroundImageUrl: SanitizeAssetUrl(themePayload.screenBackgroundImageUrl) || null,
        mobileBackgroundImageUrl: SanitizeAssetUrl(themePayload.mobileBackgroundImageUrl) || null,
        logoImageUrl: SanitizeAssetUrl(themePayload.logoImageUrl) || null,
        j1SpriteBackUrl: SanitizeAssetUrl(themePayload.j1SpriteBackUrl) || null,
        j1SpriteFrontUrl: SanitizeAssetUrl(themePayload.j1SpriteFrontUrl) || null,
        j1SpriteShootUrl: SanitizeAssetUrl(themePayload.j1SpriteShootUrl) || null,
        j1SpriteVictoryUrl: SanitizeAssetUrl(themePayload.j1SpriteVictoryUrl) || null,
        j2SpriteBackUrl: SanitizeAssetUrl(themePayload.j2SpriteBackUrl) || null,
        j2SpriteFrontUrl: SanitizeAssetUrl(themePayload.j2SpriteFrontUrl) || null,
        j2SpriteShootUrl: SanitizeAssetUrl(themePayload.j2SpriteShootUrl) || null,
        j2SpriteVictoryUrl: SanitizeAssetUrl(themePayload.j2SpriteVictoryUrl) || null
    };

    return await CreateThemeDL(sanitizedPayload, resolvedRoomId || null);
};

/**
 * Valida y actualiza un tema visual existente de Reacción Rápida.
 * 
 * @param {string} themeIdentifier - Identificador GUID del tema.
 * @param {Object} themePayload - Datos modificados del tema.
 * @param {string|null} [explicitRoomId=null] - Identificador opcional de sala.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Resultado de la actualización.
 */
export const UpdateThemeBL = async (themeIdentifier, themePayload, explicitRoomId = null) => {
    const rawId = themeIdentifier !== undefined && themeIdentifier !== null && themeIdentifier !== '' ? themeIdentifier : themePayload?.id;
    const sanitizedThemeId = SanitizeIdentifier(rawId);
    if (!sanitizedThemeId) {
        return {
            success: false,
            message: 'El identificador del tema es obligatorio.'
        };
    }

    if (!themePayload || typeof themePayload !== 'object') {
        return {
            success: false,
            message: 'El cuerpo de datos para actualizar el tema es obligatorio.'
        };
    }

    const validationResult = ValidateQuickReactionTheme(themePayload);
    if (!validationResult.isValid) {
        return {
            success: false,
            message: validationResult.message
        };
    }

    const trimmedName = SanitizeText(themePayload.name, 200);
    const resolvedRoomId = SanitizeRoomId(themePayload.roomId || explicitRoomId || null);
    const resolvedOperatorId = SanitizeIdentifier(themePayload.operatorId || null);

    const sanitizedPayload = {
        name: trimmedName,
        roomId: resolvedRoomId || null,
        operatorId: resolvedOperatorId || null,
        screenBackgroundImageUrl: SanitizeAssetUrl(themePayload.screenBackgroundImageUrl) || null,
        mobileBackgroundImageUrl: SanitizeAssetUrl(themePayload.mobileBackgroundImageUrl) || null,
        logoImageUrl: SanitizeAssetUrl(themePayload.logoImageUrl) || null,
        j1SpriteBackUrl: SanitizeAssetUrl(themePayload.j1SpriteBackUrl) || null,
        j1SpriteFrontUrl: SanitizeAssetUrl(themePayload.j1SpriteFrontUrl) || null,
        j1SpriteShootUrl: SanitizeAssetUrl(themePayload.j1SpriteShootUrl) || null,
        j1SpriteVictoryUrl: SanitizeAssetUrl(themePayload.j1SpriteVictoryUrl) || null,
        j2SpriteBackUrl: SanitizeAssetUrl(themePayload.j2SpriteBackUrl) || null,
        j2SpriteFrontUrl: SanitizeAssetUrl(themePayload.j2SpriteFrontUrl) || null,
        j2SpriteShootUrl: SanitizeAssetUrl(themePayload.j2SpriteShootUrl) || null,
        j2SpriteVictoryUrl: SanitizeAssetUrl(themePayload.j2SpriteVictoryUrl) || null
    };

    return await UpdateThemeDL(sanitizedThemeId, sanitizedPayload, resolvedRoomId || null);
};

/**
 * Elimina un tema personalizado de Reacción Rápida.
 * 
 * @param {string} themeIdentifier - Identificador GUID del tema a eliminar.
 * @param {string|null} [roomId=null] - Identificador opcional de la sala.
 * @returns {Promise<{success: boolean, message?: string}>} Confirmación de eliminación.
 */
export const DeleteThemeBL = async (themeIdentifier, roomId = null) => {
    const sanitizedThemeId = SanitizeIdentifier(themeIdentifier);
    if (!sanitizedThemeId) {
        return {
            success: false,
            message: 'El identificador del tema a eliminar es obligatorio.'
        };
    }

    const sanitizedRoomId = SanitizeRoomId(roomId);
    return await DeleteThemeDL(sanitizedThemeId, sanitizedRoomId || null);
};

/**
 * Clona un tema existente generando una nueva copia editable.
 * 
 * @param {string} themeIdentifier - Identificador GUID del tema original.
 * @param {string|null} [newThemeName=null] - Nombre opcional para la copia clonada.
 * @param {string|null} [roomId=null] - Identificador opcional de la sala.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Tema clonado.
 */
export const CloneThemeBL = async (themeIdentifier, newThemeName = null, roomId = null) => {
    const sanitizedThemeId = SanitizeIdentifier(themeIdentifier);
    if (!sanitizedThemeId) {
        return {
            success: false,
            message: 'El identificador del tema a clonar es obligatorio.'
        };
    }

    const sanitizedName = newThemeName ? SanitizeText(newThemeName, 200) : null;
    const sanitizedRoomId = SanitizeRoomId(roomId);
    return await CloneThemeDL(sanitizedThemeId, sanitizedName, sanitizedRoomId || null);
};

// =========================================================================
// ENVOLTORIOS DE CONFIGURACIÓN E HISTORIAL DE PARTIDAS
// =========================================================================

/**
 * Obtiene la configuración de Reacción Rápida para una sala o por defecto.
 * 
 * @param {string|null} [roomId=null] - Identificador GUID opcional de la sala.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Configuración activa.
 */
export const GetConfigBL = async (roomId = null) => {
    const sanitizedRoomId = SanitizeRoomId(roomId);
    return await GetConfigDL(sanitizedRoomId || null);
};

/**
 * Guarda o actualiza la configuración de juego de Reacción Rápida para una sala o globalmente.
 * 
 * @param {Object} configPayload - Datos de configuración a persistir.
 * @param {string|null} [roomId=null] - Identificador opcional de la sala.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Configuración guardada.
 */
export const SaveConfigBL = async (configPayload, roomId = null) => {
    if (!configPayload || typeof configPayload !== 'object') {
        return {
            success: false,
            message: 'El cuerpo de datos de configuración es obligatorio.'
        };
    }

    const effectiveRoomId = SanitizeRoomId(roomId || configPayload.roomId);

    const sanitizedPayload = {
        roomId: effectiveRoomId || null,
        activeThemeId: SanitizeIdentifier(configPayload.activeThemeId) || null,
        enableEarlyFault: Boolean(configPayload.enableEarlyFault),
        enableSpamCooldown: Boolean(configPayload.enableSpamCooldown),
        spamCooldownMs: Math.max(50, Math.min(5000, Number(configPayload.spamCooldownMs) || 300)),
        timingMode: configPayload.timingMode === 2 || configPayload.timingMode === 'ClientOffsetSync'
            ? 'ClientOffsetSync'
            : 'ServerArrival',
        minWaitDelayMs: Math.max(500, Math.min(30000, Number(configPayload.minWaitDelayMs) || 3000)),
        maxWaitDelayMs: Math.max(1000, Math.min(60000, Number(configPayload.maxWaitDelayMs) || 6000))
    };

    return await SaveConfigDL(sanitizedPayload, effectiveRoomId || null);
};

/**
 * Obtiene el historial de partidas disputadas en una sala o estadio.
 * 
 * @param {string|null} [roomId=null] - Identificador GUID opcional de la sala.
 * @param {number} [limitQuantity=20] - Cantidad máxima de registros históricos a recuperar.
 * @returns {Promise<{success: boolean, data?: Array<Object>, message?: string}>} Colección de partidas históricas.
 */
export const GetMatchHistoryBL = async (roomId = null, limitQuantity = 20) => {
    const sanitizedRoomId = SanitizeRoomId(roomId);
    const limit = Math.max(1, Math.min(100, Number(limitQuantity) || 20));
    return await GetMatchHistoryDL(sanitizedRoomId || null, limit);
};

/**
 * Registra y persiste el resultado final de un duelo de Reacción Rápida.
 * 
 * @param {Object} matchResultPayload - Métricas finales de la partida.
 * @param {string|null} [roomId=null] - Identificador opcional de la sala.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Resultado guardado.
 */
export const SaveMatchResultBL = async (matchResultPayload, roomId = null) => {
    if (!matchResultPayload || typeof matchResultPayload !== 'object') {
        return {
            success: false,
            message: 'El cuerpo de datos del resultado de la partida es obligatorio.'
        };
    }

    const effectiveRoomId = SanitizeRoomId(roomId || matchResultPayload.roomId);
    return await SaveMatchResultDL(matchResultPayload, effectiveRoomId || null);
};

// =========================================================================
// ENVOLTORIOS DE CARGA MULTIMEDIA
// =========================================================================

/**
 * Sube y optimiza un archivo multimedia para Reacción Rápida con validación defensiva en el cliente.
 * 
 * @param {File|Blob|FormData} fileOrFormData - Archivo a subir.
 * @param {string|null} [roomId=null] - Identificador opcional de sala.
 * @returns {Promise<{success: boolean, data?: Object, url?: string, message?: string}>} URL del archivo procesado.
 */
export const UploadAssetBL = async (fileOrFormData, roomId = null) => {
    if (!fileOrFormData) {
        return {
            success: false,
            message: 'No se ha seleccionado ningún archivo para subir.'
        };
    }

    // Validación defensiva si es un objeto File o Blob nativo
    if (fileOrFormData instanceof File || (typeof Blob !== 'undefined' && fileOrFormData instanceof Blob)) {
        const fileValidation = ValidateUploadFile(fileOrFormData);
        if (!fileValidation.isValid) {
            return {
                success: false,
                message: fileValidation.message
            };
        }
    }

    const sanitizedRoomId = SanitizeRoomId(roomId);
    return await UploadAssetDL(fileOrFormData, sanitizedRoomId || null);
};

/**
 * Sube y optimiza un lote de archivos multimedia para sprites y fondos de Reacción Rápida.
 * 
 * @param {Array<File>|FileList|FormData} filesOrFormData - Colección de archivos a procesar.
 * @param {string|null} [roomId=null] - Identificador opcional de sala.
 * @returns {Promise<{success: boolean, data?: Object, urls?: Array<string>, message?: string}>} Reporte de procesamiento.
 */
export const UploadBatchMediaBL = async (filesOrFormData, roomId = null) => {
    if (!filesOrFormData) {
        return {
            success: false,
            message: 'No se han proporcionado archivos para procesar en lote.'
        };
    }

    // Validación defensiva del lote
    if (Array.isArray(filesOrFormData) || (typeof FileList !== 'undefined' && filesOrFormData instanceof FileList)) {
        const batchValidation = ValidateBatchUploadFiles(filesOrFormData);
        if (!batchValidation.isValid) {
            return {
                success: false,
                message: batchValidation.message
            };
        }
    }

    const sanitizedRoomId = SanitizeRoomId(roomId);
    return await UploadBatchMediaDL(filesOrFormData, sanitizedRoomId || null);
};

// =========================================================================
// ENVOLTORIOS DE CONTROL DE SALA EN VIVO
// =========================================================================

/**
 * Proyecta la portada de Reacción Rápida.
 * 
 * @param {string|null} [roomId=null] - Identificador GUID opcional de la sala.
 * @param {string|null} [themeIdentifier=null] - Identificador GUID del tema.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Estado en vivo.
 */
export const ShowCoverBL = async (roomId = null, themeIdentifier = null) => {
    const sanitizedRoomId = SanitizeRoomId(roomId);
    const sanitizedThemeId = SanitizeIdentifier(themeIdentifier);
    return await ShowCoverDL(sanitizedRoomId || null, sanitizedThemeId || null);
};

/**
 * Proyecta la escena de combate (1 vs 1) y prepara los slots de los jugadores.
 * 
 * @param {string|null} [roomId=null] - Identificador GUID opcional de la sala.
 * @param {string|null} [themeIdentifier=null] - Identificador GUID del tema.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Estado en vivo.
 */
export const ShowGameSceneBL = async (roomId = null, themeIdentifier = null) => {
    const sanitizedRoomId = SanitizeRoomId(roomId);
    const sanitizedThemeId = SanitizeIdentifier(themeIdentifier);
    return await ShowGameSceneDL(sanitizedRoomId || null, sanitizedThemeId || null);
};

/**
 * Inicia la secuencia oficial de partida autoritativa.
 * 
 * @param {string|null} [roomId=null] - Identificador GUID opcional de la sala.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Estado de la secuencia iniciada.
 */
export const StartMatchBL = async (roomId = null) => {
    const sanitizedRoomId = SanitizeRoomId(roomId);
    return await StartMatchDL(sanitizedRoomId || null);
};

/**
 * Reinicia el duelo para revancha inmediata conservando participantes y tema activo.
 * 
 * @param {string|null} [roomId=null] - Identificador GUID opcional de la sala.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Estado reiniciado.
 */
export const ResetMatchBL = async (roomId = null) => {
    const sanitizedRoomId = SanitizeRoomId(roomId);
    return await ResetMatchDL(sanitizedRoomId || null);
};

/**
 * Finaliza anticipadamente el duelo por orden del administrador.
 * 
 * @param {string|null} [roomId=null] - Identificador GUID opcional de la sala.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Resultado final.
 */
export const FinishEarlyBL = async (roomId = null) => {
    const sanitizedRoomId = SanitizeRoomId(roomId);
    return await FinishEarlyDL(sanitizedRoomId || null);
};

/**
 * Consulta el estado actual en memoria de la partida.
 * 
 * @param {string|null} [roomId=null] - Identificador GUID opcional de la sala.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Estado autoritativo en vivo.
 */
export const GetMatchStateBL = async (roomId = null) => {
    const sanitizedRoomId = SanitizeRoomId(roomId);
    return await GetMatchStateDL(sanitizedRoomId || null);
};

// =========================================================================
// ENVOLTORIOS DE INTERACCIÓN MÓVIL (FANS)
// =========================================================================

/**
 * Selecciona el personaje/bando ("player1" o "player2") para el aficionado móvil con saneamiento defensivo.
 * 
 * @param {string|null} [roomId=null] - Identificador GUID opcional de la sala.
 * @param {string} side - Bando elegido ("player1" | "player2" | "J1" | "J2").
 * @param {string} [playerName=''] - Nombre del jugador.
 * @param {string} [connectionId=''] - Identificador SignalR de conexión.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Confirmación de selección de bando.
 */
export const SelectPlayerSideBL = async (roomId = null, side = '', playerName = '', connectionId = '') => {
    const sanitizedRoomId = SanitizeRoomId(roomId);
    const rawSide = String(side || '').toLowerCase().trim();
    const validSides = ['player1', 'player2', 'j1', 'j2', 'p1', 'p2', 'left', 'right', '1', '2'];
    if (!validSides.includes(rawSide)) {
        return {
            success: false,
            message: 'El bando o lado seleccionado no es válido.'
        };
    }

    const sanitizedPlayerName = SanitizePlayerName(playerName);
    const sanitizedConnectionId = SanitizeIdentifier(connectionId);

    const response = await SelectPlayerSideDL(sanitizedRoomId || null, rawSide, sanitizedPlayerName, sanitizedConnectionId);

    if (response.success && sanitizedRoomId) {
        SavePlayerSessionData(sanitizedRoomId, {
            side: rawSide,
            playerName: sanitizedPlayerName,
            connectionId: sanitizedConnectionId
        });
    }

    return response;
};

/**
 * Registra la pulsación/disparo táctil del aficionado móvil.
 * 
 * @param {string|null} [roomId=null] - Identificador GUID opcional de la sala.
 * @param {string} [connectionId=''] - Identificador SignalR de la conexión.
 * @param {number} [clientCalculatedTimeMs=0] - Tiempo de reacción medido localmente en ms.
 * @param {string} [side=''] - Bando o lado del participante ("player1" | "player2").
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Estado actualizado tras el tap.
 */
export const SendTapBL = async (roomId = null, connectionId = '', clientCalculatedTimeMs = 0, side = '') => {
    const sanitizedRoomId = SanitizeRoomId(roomId);
    const sanitizedConnectionId = SanitizeIdentifier(connectionId);
    const safeCalculatedTimeMs = Math.max(0, Math.min(300000, Number(clientCalculatedTimeMs) || 0));
    const cleanSide = String(side || '').trim().toLowerCase();

    // Disparar retroalimentación háptica local de 20ms inmediatamente (0 latencia)
    TriggerHapticFeedback('tap');

    return await SendTapDL(sanitizedRoomId || null, sanitizedConnectionId, safeCalculatedTimeMs, cleanSide);
};

/**
 * Actualiza en tiempo real los nombres de los jugadores y/o el modo de pantalla desde el panel de operador.
 * 
 * @param {string|null} [roomId=null] - Identificador GUID opcional de la sala.
 * @param {string} [player1Name=''] - Nombre personalizado para Jugador 1.
 * @param {string} [player2Name=''] - Nombre personalizado para Jugador 2.
 * @param {string} [displayMode='single_target'] - Modo de pantalla ("single_target" | "dual_duel").
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Confirmación de actualización.
 */
export const UpdatePlayersAndModeBL = async (roomId = null, player1Name = '', player2Name = '', displayMode = 'single_target') => {
    const sanitizedRoomId = SanitizeRoomId(roomId);
    const cleanP1 = SanitizePlayerName(player1Name);
    const cleanP2 = SanitizePlayerName(player2Name);
    const cleanMode = (displayMode === 'dual_duel' || displayMode === 'dual') ? 'dual_duel' : 'single_target';

    return await UpdatePlayersAndModeDL(sanitizedRoomId || null, cleanP1, cleanP2, cleanMode);
};

/**
 * Desvincula al jugador aficionado al salir de la sala.
 * 
 * @param {string|null} [roomId=null] - Identificador GUID opcional de la sala.
 * @param {string} [connectionId=''] - Identificador SignalR de la conexión.
 * @returns {Promise<{success: boolean, message?: string}>} Confirmación de desvinculación.
 */
export const UnregisterFanBL = async (roomId = null, connectionId = '') => {
    const sanitizedRoomId = SanitizeRoomId(roomId);
    const sanitizedConnectionId = SanitizeIdentifier(connectionId);

    // Limpiar sessionStorage local
    if (sanitizedRoomId) {
        ClearPlayerSessionData(sanitizedRoomId);
    }

    return await UnregisterFanDL(sanitizedRoomId || null, sanitizedConnectionId);
};

// Aliases semánticos para interoperabilidad fluida
export const GetThemesQuickReaction = GetThemesBL;
export const GetThemeByIdQuickReaction = GetThemeByIdBL;
export const CreateThemeQuickReaction = CreateThemeBL;
export const UpdateThemeQuickReaction = UpdateThemeBL;
export const DeleteThemeQuickReaction = DeleteThemeBL;
export const CloneThemeQuickReaction = CloneThemeBL;
export const GetConfigQuickReaction = GetConfigBL;
export const SaveConfigQuickReaction = SaveConfigBL;
export const GetMatchHistoryQuickReaction = GetMatchHistoryBL;
export const GetMatchesHistoryQuickReaction = GetMatchHistoryBL;
export const SaveMatchResultQuickReaction = SaveMatchResultBL;
export const UploadMediaQuickReaction = UploadAssetBL;
export const UploadAssetQuickReaction = UploadAssetBL;
export const UploadBatchMediaQuickReaction = UploadBatchMediaBL;
export const ShowCoverQuickReaction = ShowCoverBL;
export const ShowGameQuickReaction = ShowGameSceneBL;
export const ShowGameSceneQuickReaction = ShowGameSceneBL;
export const StartMatchSequenceQuickReaction = StartMatchBL;
export const StartMatchQuickReaction = StartMatchBL;
export const ResetMatchQuickReaction = ResetMatchBL;
export const FinishEarlyQuickReaction = FinishEarlyBL;
export const GetMatchStateQuickReaction = GetMatchStateBL;

export default {
    // Seguridad y Validación
    ALLOWED_IMAGE_MIME_TYPES,
    ALLOWED_IMAGE_EXTENSIONS,
    MAX_UPLOAD_FILE_SIZE_BYTES,
    MAX_BATCH_FILES_COUNT,
    MAX_BATCH_TOTAL_SIZE_BYTES,
    SanitizeIdentifier,
    SanitizeRoomId,
    SanitizeText,
    SanitizePlayerName,
    SanitizeAssetUrl,
    ValidateUploadFile,
    ValidateBatchUploadFiles,
    ValidateQuickReactionTheme,
    // Sesión
    SavePlayerSessionData,
    GetPlayerSessionData,
    ClearPlayerSessionData,
    // Web Audio
    UnlockAudioContext,
    AttachAutoUnlockAudioListeners,
    SynthesizeQuickReactionSound,
    // Screen Wake Lock
    RequestScreenWakeLock,
    ReleaseScreenWakeLock,
    // Háptico
    TriggerHapticFeedback,
    // NTP
    CalibrateNtpOffset,
    // CRUD Temas
    GetThemesBL,
    GetThemeByIdBL,
    CreateThemeBL,
    UpdateThemeBL,
    DeleteThemeBL,
    CloneThemeBL,
    // Configuración e Historial
    GetConfigBL,
    SaveConfigBL,
    GetMatchHistoryBL,
    SaveMatchResultBL,
    // Multimedia
    UploadAssetBL,
    UploadBatchMediaBL,
    // Control en Vivo
    ShowCoverBL,
    ShowGameSceneBL,
    StartMatchBL,
    ResetMatchBL,
    FinishEarlyBL,
    GetMatchStateBL,
    // Móvil
    SelectPlayerSideBL,
    SendTapBL,
    UnregisterFanBL,
    // Aliases
    ValidateTheme: ValidateQuickReactionTheme,
    GetThemesQuickReaction,
    CreateThemeQuickReaction,
    UpdateThemeQuickReaction,
    DeleteThemeQuickReaction,
    CloneThemeQuickReaction,
    GetConfigQuickReaction,
    SaveConfigQuickReaction,
    GetMatchesHistoryQuickReaction,
    UploadMediaQuickReaction,
    UploadBatchMediaQuickReaction,
    ShowCoverQuickReaction,
    ShowGameQuickReaction,
    StartMatchSequenceQuickReaction,
    ResetMatchQuickReaction
};

// Aliases para compatibilidad hacia atrás
export const GetThemes = GetThemesBL;
export const GetTheme = GetThemeByIdBL;
export const SaveTheme = UpdateThemeBL;
export const CreateTheme = CreateThemeBL;
export const DeleteTheme = DeleteThemeBL;
export const CloneTheme = CloneThemeBL;
export const GetConfig = GetConfigBL;
export const SaveConfig = SaveConfigBL;
export const GetMatchesHistory = GetMatchHistoryBL;
export const ShowCover = ShowCoverBL;
export const ShowGame = ShowGameSceneBL;
export const StartMatch = StartMatchBL;
export const ResetMatch = ResetMatchBL;
export const GetRealtimeState = GetMatchStateBL;
export const SaveFanSessionData = SavePlayerSessionData;
export const GetFanSessionData = GetPlayerSessionData;
export const ClearFanSessionData = ClearPlayerSessionData;
export const FAN_SESSION_STORAGE_KEY = 'quick_reaction_player_session';

